/**
 * /dashboard/offboarding/$id — Offboarding cycle detail (G4.6 + G4.7).
 *
 * Layout (plan §7 — single column, stacked sections):
 *   1. Header strip — employee, status, reason, LWD, notice, initiator
 *   2. State-machine action panel — legal next states + close button
 *   3. Task checklist — per-task status + assignee
 *   4. ICC seat handoff (conditional — when icc_seat_handoff task exists)
 *   5. F&F compute panel — pre-eligible card / inputs form / immutable
 *      snapshot view (G4.7)
 *   6. Cycle letter library — generate relieving / experience / FF
 *      statement letters, send, download (G4.7)
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/offboarding/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useOffboardingCycle } from "@/hooks/use-offboarding";
import { HrModuleShell } from "@/components/shared/hr-module-shell";
import { OffboardingRoleGate } from "@/components/offboarding/offboarding-role-gate";
import { CycleHeader } from "@/components/offboarding/cycle-header";
import { StateMachineActions } from "@/components/offboarding/state-machine-actions";
import { TaskChecklist } from "@/components/offboarding/task-checklist";
import { ICCHandoffPanel } from "@/components/offboarding/icc-handoff-panel";
import { FFComputePanel } from "@/components/offboarding/ff-compute-panel";
import { CycleLetterLibrary } from "@/components/offboarding/cycle-letter-library";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { statusLabel } from "@/lib/offboarding-format";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/hr/offboarding/$id")({
  component: OffboardingCycleDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function OffboardingCycleDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useOffboardingCycle(numId, validId);

  const cycle = q.data?.cycle;
  const tasks = q.data?.tasks ?? [];
  const iccHandoffTask = tasks.find(
    (t) => t.task_type === "icc_seat_handoff",
  );

  return (
    <HrModuleShell
      title="Offboarding cycle"
      description={cycle ? `Status: ${statusLabel(cycle.status)}` : undefined}
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/hr/offboarding" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <OffboardingRoleGate>
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid cycle id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid cycle id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this cycle</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/hr/offboarding" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to offboarding
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && cycle && (
        <div className="space-y-5">
          <CycleHeader cycle={cycle} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <StateMachineActions cycle={cycle} tasks={tasks} />
            </CardContent>
          </Card>

          <TaskChecklist tasks={tasks} />

          {iccHandoffTask && (
            <ICCHandoffPanel
              task={iccHandoffTask}
              exitingEmployeeId={cycle.employee_id}
            />
          )}

          <FFComputePanel cycle={cycle} />

          <CycleLetterLibrary cycle={cycle} />
        </div>
      )}
      </OffboardingRoleGate>
    </HrModuleShell>
  );
}
